import React from 'react';

import { Alert, Box, Chip, Stack, Tooltip, Typography } from '@mui/material';

import { CollectionMetrics as CollectionMetricsType } from '~/services/ExperiencesService';

import { formatCurrency, formatNumber } from '../utils';

interface Props {
  metrics: CollectionMetricsType | null | undefined;
  isActive: boolean;
  activatedAt?: string | null;
}

export default function CollectionMetrics({ metrics, isActive, activatedAt }: Props) {
  if (!metrics) {
    return (
      <Box mb={2}>
        <Alert severity="info" variant="outlined">
          Metrics not yet available. Data syncs daily at 6am Sydney.
        </Alert>
      </Box>
    );
  }

  const historical = metrics?.historical;
  const last7d = metrics?.last7d;

  if (!historical || !last7d) {
    return (
      <Box mb={2}>
        <Alert severity="info" variant="outlined">
          Metrics not yet available. Data syncs daily at 6am Sydney.
        </Alert>
      </Box>
    );
  }

  const activatedLabel = activatedAt ? new Date(activatedAt).toLocaleDateString('en-AU') : 'N/A';
  const updatedLabel = last7d.updatedAt
    ? new Date(last7d.updatedAt).toLocaleString('en-AU', { dateStyle: 'short', timeStyle: 'short' })
    : 'N/A';

  return (
    <Box mb={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="subtitle2" fontWeight="bold" color="text.secondary">
          ALL TIME (since {activatedLabel})
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          {!isActive && <Chip label="Last snapshot" size="small" color="warning" variant="outlined" />}
          <Tooltip title={`Last sync: ${last7d.updatedAt || 'N/A'}`}>
            <Typography variant="caption" color="text.secondary">
              Updated: {updatedLabel}
            </Typography>
          </Tooltip>
        </Stack>
      </Stack>

      <Stack direction="row" spacing={2} mb={2}>
        <MetricCard
          label="Views"
          value={formatNumber(historical.sessionsWithViews)}
          daily={`${formatNumber(Math.round(historical.dailyAvgViews))}/d`}
        />
        <MetricCard
          label="Orders"
          value={formatNumber(historical.orders)}
          daily={`${historical.dailyAvgOrders.toFixed(1)}/d`}
        />
        <MetricCard
          label="Gross Sales"
          value={formatCurrency(historical.grossSalesAud)}
          daily={`${formatCurrency(historical.grossSalesAud / (historical.totalDays || 1))}/d`}
        />
        <MetricCard label="Margin" value={formatCurrency(historical.marginAud)} />
      </Stack>

      <Typography variant="subtitle2" fontWeight="bold" color="text.secondary" mb={1}>
        LAST 7 DAYS
      </Typography>

      <Stack direction="row" spacing={2}>
        <MetricCard label="Views" value={formatNumber(last7d.sessionsWithViews)} />
        <MetricCard label="Clicks" value={formatNumber(last7d.clicks)} />
        <MetricCard label="Orders" value={formatNumber(last7d.orders)} />
        <MetricCard label="Gross Sales" value={formatCurrency(last7d.grossSalesAud)} />
        <MetricCard label="Margin" value={formatCurrency(last7d.marginAud)} />
      </Stack>
    </Box>
  );
}

function MetricCard({ label, value, daily }: { label: string; value: string; daily?: string }) {
  return (
    <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1, minWidth: 120, flex: 1 }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body1" fontWeight="bold">
        {value}
        {daily && (
          <Typography component="span" variant="caption" color="text.secondary" ml={0.5}>
            ({daily})
          </Typography>
        )}
      </Typography>
    </Box>
  );
}
